import { memo, useEffect, useState } from "react";
import {
  LayoutChangeEvent,
  Platform,
  ScrollView,
  Text,
  TextStyle,
  View,
} from "react-native";
import Animated, {
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withDelay,
  withTiming,
} from "react-native-reanimated";
import { useMarqueeController } from "./MarqueeController";

interface MarqueeTextProps {
  children: string;
  style?: TextStyle;
  delay?: number; // initial delay in milliseconds before starting to follow the controller
}

const MarqueeText = ({ children, style, delay = 0 }: MarqueeTextProps) => {
  const [containerWidth, setContainerWidth] = useState(0);
  const [textWidth, setTextWidth] = useState(0);
  const { progress } = useMarqueeController();
  const isActive = useSharedValue(delay === 0 ? 1 : 0);

  const needsMarquee = textWidth > containerWidth && containerWidth > 0;

  // Activate the marquee after delay
  useEffect(() => {
    if (delay > 0) {
      isActive.value = withDelay(delay, withTiming(1, { duration: 0 }));
    }
  }, [delay, isActive]);

  // Map the shared progress to this marquee's translation
  const translateX = useDerivedValue(() => {
    if (!needsMarquee || isActive.value === 0) {
      return 0;
    }

    const distance = textWidth - containerWidth;
    // progress goes 0 -> 1 -> 0, map this to 0 -> -distance -> 0
    return -distance * progress.value;
  }, [needsMarquee, textWidth, containerWidth]);

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: translateX.value }],
  }));

  const onContainerLayout = (event: LayoutChangeEvent) => {
    setContainerWidth(event.nativeEvent.layout.width);
  };

  const onTextLayout = (event: LayoutChangeEvent) => {
    setTextWidth(event.nativeEvent.layout.width);
  };

  const containerHeight =
    Platform.OS === "web" ? style?.lineHeight || style?.fontSize : undefined;
  const textContainerWidth =
    Platform.OS === "web" ? "100%" : textWidth || "auto";

  return (
    <>
      {/* Hidden ScrollView for measuring true text width without wrapping */}
      <ScrollView
        horizontal
        style={{ opacity: 0, position: "absolute", zIndex: -9999 }}
      >
        <View onLayout={onTextLayout}>
          <Text
            allowFontScaling={false}
            maxFontSizeMultiplier={1}
            style={style}
          >
            {children}
          </Text>
        </View>
      </ScrollView>

      {/* Visible container */}
      <View onLayout={onContainerLayout}>
        <View
          style={{
            overflow: "hidden",
            height: containerHeight,
          }}
        >
          <Animated.View
            style={[
              animatedStyle,
              {
                width: textContainerWidth,
              },
            ]}
          >
            <Text
              allowFontScaling={false}
              maxFontSizeMultiplier={1}
              style={[
                style,
                Platform.OS === "web"
                  ? {
                      //@ts-expect-error - whiteSpace is not a valid style property for react-native
                      whiteSpace: "nowrap",
                    }
                  : {},
              ]}
            >
              {children}
            </Text>
          </Animated.View>
        </View>
      </View>
    </>
  );
};

export default memo(MarqueeText);
